<script setup lang="ts">
import { inject, onBeforeMount, ref, Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import useVuelidate from '@vuelidate/core';
import { helpers, required } from '@vuelidate/validators';
import { STATUS_CLOSED, STATUS_OPEN, GQT_ADMIN } from '@/constants';
import { usePackageManagementStore } from '@/store/PackageManagementStore';
import { useViewComposable } from '@/views/composable';
import { UserInfo } from '@/modules/user/UserInfo';
import { useUserInfo } from '@/modules/user/useUserInfo';
import { useToast } from 'primevue/usetoast';
import AutoComplete, { AutoCompleteCompleteEvent, AutoCompleteOptionSelectEvent } from 'primevue/autocomplete';
import Button from 'primevue/button';
import Fluid from 'primevue/fluid';
import Select from 'primevue/select';
import ToggleSwitch from 'primevue/toggleswitch';
import InputText from 'primevue/inputtext';
import LoadingSpinner from '@/components/LoadingSpinner.vue';

const dialogRef = inject('dialogRef') as Ref;
const dataEdit = dialogRef?.value.options.data?.data;
const airports = dialogRef?.value.options.data?.airports;
const updateData = dialogRef?.value?.options?.emits?.updateData;

const { t } = useI18n({ useScope: 'global' });
const toast = useToast();
const userInfo = useUserInfo();
const PackageManagementStore = usePackageManagementStore();
const { brochureListOption } = useViewComposable();
const currentUser = ref<UserInfo>();

const defaultDestination = {
  airportCode: '',
  airportName: '',
  airportNameFr: '',
  cityName: '',
  cityNameFr: '',
  code: '',
  country: '',
  countryFr: '',
  destType: 'MANUAL',
  durations: null,
  gateways: null,
  groupDes: null,
  hotels: null,
  name: '',
  nameFr: '',
  pk: null,
  region: '',
  regionFr: '',
  sk: '',
  status: STATUS_CLOSED,
  brochure: '',
  statusEdit: true
};

const initData = () => {
  if (!dataEdit) {
    return defaultDestination;
  }
  return {
    airportCode: dataEdit?.airportCode,
    airportName: dataEdit?.airportName,
    airportNameFr: dataEdit?.airportNameFr,
    cityName: dataEdit?.name,
    cityNameFr: dataEdit?.nameFr,
    code: dataEdit?.code,
    country: dataEdit?.country,
    countryFr: dataEdit?.countryFr,
    destType: dataEdit?.destType,
    durations: dataEdit?.durations,
    gateways: dataEdit?.gateways,
    groupDes: dataEdit?.groupDes,
    hotels: dataEdit?.hotels,
    name: dataEdit?.name,
    nameFr: dataEdit?.nameFr,
    pk: dataEdit?.pk,
    region: dataEdit?.region,
    regionFr: dataEdit?.regionFr,
    sk: dataEdit?.sk,
    status: dataEdit?.status,
    brochure: dataEdit?.brochure,
    statusEdit: dataEdit?.statusEdit
  };
};
const airportSelected = ref(initData());
const isLoading = ref(false);
const filterAirport = ref<any[]>([]);

const rules = {
  airportCode: {
    required: helpers.withMessage(t('validate.required'), required)
  },
  airportName: {
    required: helpers.withMessage(t('validate.required'), required)
  },
  airportNameFr: {
    required: helpers.withMessage(t('validate.required'), required)
  },
  name: {
    required: helpers.withMessage(t('validate.required'), required)
  },
  nameFr: {
    required: helpers.withMessage(t('validate.required'), required)
  },
  region: {
    required: helpers.withMessage(t('validate.required'), required)
  },
  regionFr: {
    required: helpers.withMessage(t('validate.required'), required)
  },
  country: {
    required: helpers.withMessage(t('validate.required'), required)
  },
  countryFr: {
    required: helpers.withMessage(t('validate.required'), required)
  },
  code: {
    required: helpers.withMessage(t('validate.required'), required)
  }
};

const v$ = useVuelidate(rules, airportSelected);

const submit = async () => {
  v$.value.$validate();
  if (!v$.value.$errors.length) {
    PackageManagementStore.addEditDestinationStatus(airportSelected.value)
      .then(() => {
        toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
        updateData();
        dialogRef.value.close();
      })
      .catch(() => {
        toast.add({ severity: 'error', summary: '', detail: t('notification.save_error'), life: 5000 });
      });
  }
};

const onChangeAirportCode = (event: AutoCompleteOptionSelectEvent) => {
  const selectedAir = airports.find((item) => item.airportCode === event.value.airportCode);
  if (selectedAir) {
    airportSelected.value = {
      ...airportSelected.value,
      airportCode: selectedAir.airportCode,
      airportName: selectedAir.airportName,
      airportNameFr: selectedAir.airportNameFR,
      name: selectedAir.cityName,
      nameFr: selectedAir.cityNameFr,
      region: selectedAir.region,
      regionFr: selectedAir.regionFr,
      code: dataEdit ? dataEdit?.code : (selectedAir.destSVId ?? ''),
      cityName: selectedAir.cityName,
      cityNameFr: selectedAir.cityNameFr,
      country: selectedAir.countryName,
      countryFr: selectedAir.countryNameFr
    };
  }
};

const onClearAirportCode = (dataEdit: any) => {
  if (!dataEdit) {
    airportSelected.value = { ...defaultDestination, airportCode: '' };
  } else {
    airportSelected.value = {
      ...initData(),
      airportCode: '',
      airportName: '',
      airportNameFr: '',
      name: '',
      nameFr: '',
      region: '',
      regionFr: '',
      cityName: '',
      cityNameFr: '',
      country: '',
      countryFr: '',
      code: dataEdit?.code
    };
  }
};

const loadData = async () => {
  isLoading.value = true;
  try {
    currentUser.value = await userInfo.getUserInfo();
  } finally {
    isLoading.value = false;
  }
};

const searchAirportCode = (event: AutoCompleteCompleteEvent) => {
  if (!event.query.trim().length) {
    filterAirport.value = [...airports.slice(0, 100)];
  } else {
    filterAirport.value = airports.filter((airport) => {
      return (
        airport?.airportCode?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        airport?.airportName?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        airport?.cityName?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        airport?.countryName?.toLowerCase().startsWith(event.query.toLowerCase())
      );
    });
  }
};

onBeforeMount(async () => {
  loadData();
});
</script>

<template>
  <div>
    <LoadingSpinner v-if="isLoading" />
    <div class="manage-destination-modal">
      <Fluid>
        <div class="field grid">
          <div class="col-6">
            <label for="destination_airport_code" class="mb-1 inline-block">{{ `${$t('management.air.airport_code')}` }}</label>
            <div class="field flex-1">
              <div>
                <AutoComplete
                  :disabled="!!dataEdit?.airportCode && currentUser?.role !== GQT_ADMIN"
                  id="destination_airport_code"
                  @item-select="onChangeAirportCode"
                  @clear="onClearAirportCode(dataEdit)"
                  v-model="airportSelected.airportCode"
                  :suggestions="filterAirport"
                  @complete="searchAirportCode($event)"
                  completeOnFocus
                  :class="v$.airportCode.$error ? 'p-invalid' : ''"
                  class="w-full"
                  forceSelection
                  :optionLabel="'airportCode'"
                >
                  <template #option="slotProps">
                    <div class="flex align-options-center">
                      <div>
                        <div>{{ slotProps.option?.airportName }}</div>
                        <div class="text-xs">{{ slotProps.option?.cityName }}, {{ slotProps.option?.region || slotProps.option?.province }}, {{ slotProps.option?.countryName }} ({{ slotProps.option?.airportCode }})</div>
                      </div>
                    </div>
                  </template>
                </AutoComplete>
                <p class="text-red-500 pt-1" v-if="v$.airportCode.$error">{{ $t('validate.required') }}</p>
              </div>
            </div>
          </div>
          <div class="col-6">
            <label for="brochure" class="mb-1 inline-block">{{ `${$t('management.package.brochure')}` }}</label>
            <Select id="brochure" class="w-full" showClear :placeholder="$t('management.package.select_brochure')" :options="brochureListOption" v-model="airportSelected.brochure" />
          </div>
        </div>
      </Fluid>
      <Fluid>
        <div class="field grid">
          <div class="col">
            <label class="mb-1" for="destination_airport_name">{{ `${$t('management.package.airport_name')} (${$t('common.english')})` }}</label>
            <InputText :disabled="!airportSelected.airportCode" :class="v$.airportName.$error && !!airportSelected.airportCode ? 'p-invalid' : ''" id="destination_airport_name" v-model="airportSelected.airportName" />
            <p class="text-red-500 pt-1" v-if="v$.airportName.$error && !!airportSelected.airportCode">{{ $t('validate.required') }}</p>
          </div>
          <div class="col">
            <label class="mb-1" for="destination_airport_name_fr">{{ `${$t('management.package.airport_name')} (${$t('common.french')})` }}</label>
            <InputText :disabled="!airportSelected.airportCode" :class="v$.airportNameFr.$error && !!airportSelected.airportCode ? 'p-invalid' : ''" id="destination_airport_name_fr" v-model="airportSelected.airportNameFr" />
            <p class="text-red-500 pt-1" v-if="v$.airportNameFr.$error && !!airportSelected.airportCode">{{ $t('validate.required') }}</p>
          </div>
        </div>
      </Fluid>
      <Fluid>
        <div class="field grid">
          <div class="col">
            <label for="destination_name">{{ `${$t('management.package.destination_name')} (${$t('common.english')})` }}</label>
            <InputText :disabled="!airportSelected.airportCode" id="destination_name" :class="v$.name.$error && !!airportSelected.airportCode ? 'p-invalid' : ''" v-model="airportSelected.name" />
            <p class="text-red-500 pt-1" v-if="v$.name.$error && !!airportSelected.airportCode">{{ $t('validate.required') }}</p>
          </div>
          <div class="col">
            <label for="destination_name_fr">{{ `${$t('management.package.destination_name')} (${$t('common.french')})` }}</label>
            <InputText :disabled="!airportSelected.airportCode" id="destination_name_fr" :class="v$.nameFr.$error && !!airportSelected.airportCode ? 'p-invalid' : ''" v-model="airportSelected.nameFr" />
            <p class="text-red-500 pt-1" v-if="v$.nameFr.$error && !!airportSelected.airportCode">{{ $t('validate.required') }}</p>
          </div>
        </div>
      </Fluid>
      <Fluid>
        <div class="field grid">
          <div class="col">
            <label for="destination_region">{{ `${$t('management.package.region')} (${$t('common.english')})` }}</label>
            <InputText :disabled="!airportSelected.airportCode" id="destination_region" :class="v$.region.$error && !!airportSelected.airportCode ? 'p-invalid' : ''" v-model="airportSelected.region" />
            <p class="text-red-500 pt-1" v-if="v$.region.$error && !!airportSelected.airportCode">{{ $t('validate.required') }}</p>
          </div>
          <div class="col">
            <label for="destination_region_fr">{{ `${$t('management.package.region')} (${$t('common.french')})` }}</label>
            <InputText :disabled="!airportSelected.airportCode" id="destination_region_fr" :class="v$.regionFr.$error && !!airportSelected.airportCode ? 'p-invalid' : ''" v-model="airportSelected.regionFr" />
            <p class="text-red-500 pt-1" v-if="v$.regionFr.$error && !!airportSelected.airportCode">{{ $t('validate.required') }}</p>
          </div>
        </div>
      </Fluid>
      <Fluid>
        <div class="field grid">
          <div class="col">
            <label for="destination_country">{{ `${$t('management.package.country')} (${$t('common.english')})` }}</label>
            <InputText :disabled="!airportSelected.airportCode" id="destination_country" :class="v$.country.$error && !!airportSelected.airportCode ? 'p-invalid' : ''" v-model="airportSelected.country" />
            <p class="text-red-500 pt-1" v-if="v$.country.$error && !!airportSelected.airportCode">{{ $t('validate.required') }}</p>
          </div>
          <div class="col">
            <label for="destination_country_fr">{{ `${$t('management.package.country')} (${$t('common.french')})` }}</label>
            <InputText :disabled="!airportSelected.airportCode" id="destination_country_fr" :class="v$.countryFr.$error && !!airportSelected.airportCode ? 'p-invalid' : ''" v-model="airportSelected.countryFr" />
            <p class="text-red-500 pt-1" v-if="v$.countryFr.$error && !!airportSelected.airportCode">{{ $t('validate.required') }}</p>
          </div>
        </div>
      </Fluid>
      <Fluid>
        <div class="field grid">
          <div class="col">
            <label for="destination_sv_id">{{ $t('management.package.destination_sv_id') }}</label>
            <InputText :disabled="!airportSelected.airportCode || !!dataEdit?.airportCode" id="destination_sv_id" :class="v$.code.$error && !!airportSelected.airportCode ? 'p-invalid' : ''" v-model="airportSelected.code" />
            <p class="text-red-500 pt-1" v-if="v$.code.$error && !!airportSelected.airportCode">{{ $t('validate.required') }}</p>
          </div>
          <div class="col">
            <label for="destination_status">{{ $t('management.package.dest_status') }}</label>
            <div class="mt-2">
              <ToggleSwitch v-model="airportSelected.status" :trueValue="STATUS_OPEN" :falseValue="STATUS_CLOSED" id="destination_status" :disabled="!airportSelected.airportCode" />
            </div>
          </div>
        </div>
      </Fluid>
      <div class="flex justify-content-center mt-3">
        <Button data-testid="submit_destination" :label="$t('voucher.submit')" @click="submit()" />
      </div>
    </div>
  </div>
</template>
